import { useCallback, useEffect, useState } from 'react';
import {
  CircularProgress,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@material-ui/core';
import { useApi } from '@backstage/core-plugin-api';
import { scaffolderPlugin } from '@backstage/plugin-scaffolder';
import {
  createScaffolderFieldExtension,
  FieldExtensionComponentProps,
} from '@backstage/plugin-scaffolder-react';
import {
  DeploymentContextEnvironment,
  DeploymentContextLocation,
  DeploymentContextNamingConvention,
  DeploymentContextOptions,
  beehiveScaffolderEnvironmentsApiRef,
} from '../../apis/scaffolderEnvironments';

export type DeploymentContextSelectedValue = {
  canonicalName: string;
  shortName: string;
  displayName: string;
};

export type DeploymentContextValue = {
  namingConvention?: string;
  environment?: DeploymentContextSelectedValue;
  location?: DeploymentContextSelectedValue;
};

function getDefaultConvention(
  conventions: DeploymentContextNamingConvention[],
) {
  return conventions.find(convention => convention.default) ?? conventions[0];
}

function getEnvironmentShortName(
  convention: DeploymentContextNamingConvention,
  environment: DeploymentContextEnvironment,
) {
  return convention.environmentNameMode === 'short'
    ? environment.shortName
    : environment.canonicalName;
}

function getLocationShortName(
  convention: DeploymentContextNamingConvention,
  location: DeploymentContextLocation,
) {
  return convention.locationNameMode === 'short'
    ? location.shortName
    : location.canonicalName;
}

function selectEnvironment(
  convention: DeploymentContextNamingConvention,
  environment: DeploymentContextEnvironment,
): DeploymentContextSelectedValue {
  return {
    canonicalName: environment.canonicalName,
    shortName: getEnvironmentShortName(convention, environment),
    displayName: environment.displayName,
  };
}

function selectLocation(
  convention: DeploymentContextNamingConvention,
  location: DeploymentContextLocation,
): DeploymentContextSelectedValue {
  return {
    canonicalName: location.canonicalName,
    shortName: getLocationShortName(convention, location),
    displayName: location.displayName,
  };
}

function findConventionForValue(
  options: DeploymentContextOptions,
  formData: DeploymentContextValue | undefined,
) {
  if (!formData?.namingConvention) {
    return undefined;
  }

  return options.namingConventions.find(
    convention => convention.id === formData.namingConvention,
  );
}

function deploymentContextValuesEqual(
  left: DeploymentContextValue | undefined,
  right: DeploymentContextValue,
) {
  return JSON.stringify(left ?? {}) === JSON.stringify(right);
}

export function EnvironmentLocationPicker(
  props: FieldExtensionComponentProps<DeploymentContextValue | undefined>,
) {
  const {
    disabled,
    formData,
    idSchema,
    onChange,
    rawErrors,
    readonly,
    required,
    schema,
  } = props;
  const environmentsApi = useApi(beehiveScaffolderEnvironmentsApiRef);
  const [options, setOptions] = useState<DeploymentContextOptions>({
    namingConventions: [],
    environments: [],
    locations: [],
  });
  const [selectedConventionId, setSelectedConventionId] = useState<string>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>();

  useEffect(() => {
    let mounted = true;

    environmentsApi
      .listDeploymentContextOptions()
      .then(items => {
        if (!mounted) {
          return;
        }

        const selectedConvention = getDefaultConvention(
          items.namingConventions,
        );

        setOptions(items);
        setSelectedConventionId(selectedConvention?.id);
        setError(undefined);
      })
      .catch(e => {
        if (!mounted) {
          return;
        }

        setOptions({ namingConventions: [], environments: [], locations: [] });
        setSelectedConventionId(undefined);
        setError(
          e instanceof Error
            ? e.message
            : 'Failed to load deployment context options',
        );
      })
      .finally(() => {
        if (mounted) {
          setLoading(false);
        }
      });

    return () => {
      mounted = false;
    };
  }, [environmentsApi]);

  const selectedConvention = options.namingConventions.find(
    convention => convention.id === selectedConventionId,
  );

  useEffect(() => {
    const convention = findConventionForValue(options, formData);

    if (convention && convention.id !== selectedConventionId) {
      setSelectedConventionId(convention.id);
    }
  }, [formData, options, selectedConventionId]);

  const selectedEnvironmentCanonicalName = formData?.environment?.canonicalName;
  const selectedLocationCanonicalName = formData?.location?.canonicalName;
  const selectedEnvironment = options.environments.find(
    environment =>
      environment.canonicalName === selectedEnvironmentCanonicalName,
  );
  const selectedLocation = options.locations.find(
    location => location.canonicalName === selectedLocationCanonicalName,
  );

  const buildValue = useCallback(
    ({
      convention = selectedConvention,
      environment = selectedEnvironment,
      location = selectedLocation,
    }: {
      convention?: DeploymentContextNamingConvention;
      environment?: DeploymentContextEnvironment;
      location?: DeploymentContextLocation;
    }): DeploymentContextValue | undefined => {
      if (!convention) {
        return undefined;
      }

      return {
        namingConvention: convention.id,
        ...(environment
          ? { environment: selectEnvironment(convention, environment) }
          : {}),
        ...(location ? { location: selectLocation(convention, location) } : {}),
      };
    },
    [selectedConvention, selectedEnvironment, selectedLocation],
  );

  useEffect(() => {
    if (!selectedConvention) {
      return;
    }

    const environment =
      selectedEnvironment ??
      (options.environments.length === 1 ? options.environments[0] : undefined);
    const location =
      selectedLocation ??
      (options.locations.length === 1 ? options.locations[0] : undefined);
    const nextValue = buildValue({
      convention: selectedConvention,
      environment,
      location,
    });

    if (
      nextValue &&
      !deploymentContextValuesEqual(formData, nextValue) &&
      (formData?.namingConvention !== selectedConvention.id ||
        (!selectedEnvironment && options.environments.length === 1) ||
        (!selectedLocation && options.locations.length === 1))
    ) {
      onChange(nextValue);
    }
  }, [
    buildValue,
    formData,
    onChange,
    options.environments,
    options.locations,
    selectedConvention,
    selectedEnvironment,
    selectedLocation,
  ]);

  const onConventionChange = (conventionId: string) => {
    const convention = options.namingConventions.find(
      item => item.id === conventionId,
    );
    setSelectedConventionId(conventionId);

    const nextValue = buildValue({ convention });
    if (nextValue) {
      onChange(nextValue);
    }
  };

  const onEnvironmentChange = (canonicalName: string) => {
    const environment = options.environments.find(
      item => item.canonicalName === canonicalName,
    );
    const nextValue = buildValue({ environment });

    if (nextValue) {
      onChange(nextValue);
    }
  };

  const onLocationChange = (canonicalName: string) => {
    const location = options.locations.find(
      item => item.canonicalName === canonicalName,
    );
    const nextValue = buildValue({ location });

    if (nextValue) {
      onChange(nextValue);
    }
  };

  const hasError = Boolean(error) || Boolean(rawErrors?.length);
  const helperText = error ?? rawErrors?.[0] ?? schema.description;
  const conventionValue = options.namingConventions.some(
    convention => convention.id === selectedConventionId,
  )
    ? selectedConventionId
    : '';
  const controlsDisabled = disabled || readonly || loading || Boolean(error);
  const conventionDisabled =
    controlsDisabled || options.namingConventions.length === 0;
  const environmentDisabled =
    controlsDisabled ||
    !selectedConvention ||
    options.environments.length === 0;
  const locationDisabled =
    controlsDisabled || !selectedConvention || options.locations.length === 0;

  return (
    <>
      <FormControl
        fullWidth
        margin="normal"
        error={Boolean(error)}
        disabled={conventionDisabled}
      >
        <InputLabel id={`${idSchema.$id}-convention-label`}>
          Naming convention
        </InputLabel>
        <Select
          labelId={`${idSchema.$id}-convention-label`}
          id={`${idSchema.$id}-convention`}
          MenuProps={{ disablePortal: true }}
          value={conventionValue}
          onChange={event => onConventionChange(event.target.value as string)}
        >
          {options.namingConventions.map(convention => (
            <MenuItem key={convention.id} value={convention.id}>
              {convention.name}
            </MenuItem>
          ))}
        </Select>
        {loading && (
          <FormHelperText component="div">
            <CircularProgress size={14} /> Loading deployment context…
          </FormHelperText>
        )}
        {!loading && options.namingConventions.length === 0 && !error && (
          <FormHelperText>
            No deployment context naming conventions are configured.
          </FormHelperText>
        )}
        {selectedConvention?.description && (
          <FormHelperText>{selectedConvention.description}</FormHelperText>
        )}
        {error && <FormHelperText>{error}</FormHelperText>}
      </FormControl>

      <FormControl
        fullWidth
        margin="normal"
        required={required}
        error={hasError}
        disabled={environmentDisabled}
      >
        <InputLabel id={`${idSchema.$id}-environment-label`}>
          Environment
        </InputLabel>
        <Select
          labelId={`${idSchema.$id}-environment-label`}
          id={`${idSchema.$id}-environment`}
          MenuProps={{ disablePortal: true }}
          value={selectedEnvironmentCanonicalName ?? ''}
          onChange={event => onEnvironmentChange(event.target.value as string)}
        >
          {options.environments.map(environment => (
            <MenuItem
              key={environment.canonicalName}
              value={environment.canonicalName}
            >
              {environment.displayName}
            </MenuItem>
          ))}
        </Select>
        {helperText && <FormHelperText>{helperText}</FormHelperText>}
      </FormControl>

      {formData?.environment && (
        <Typography variant="caption" color="textSecondary" component="div">
          {formData.environment?.shortName
            ? `Environment: ${formData.environment.shortName}`
            : 'Environment: not selected'}
        </Typography>
      )}

      <FormControl
        fullWidth
        margin="normal"
        required={required}
        error={hasError}
        disabled={locationDisabled}
      >
        <InputLabel id={`${idSchema.$id}-location-label`}>Location</InputLabel>
        <Select
          labelId={`${idSchema.$id}-location-label`}
          id={`${idSchema.$id}-location`}
          MenuProps={{ disablePortal: true }}
          value={selectedLocationCanonicalName ?? ''}
          onChange={event => onLocationChange(event.target.value as string)}
        >
          {options.locations.map(location => (
            <MenuItem
              key={location.canonicalName}
              value={location.canonicalName}
            >
              {location.displayName}
            </MenuItem>
          ))}
        </Select>
        {helperText && <FormHelperText>{helperText}</FormHelperText>}
      </FormControl>

      {formData?.location && (
        <Typography variant="caption" color="textSecondary" component="div">
          {formData.location?.shortName
            ? `Location: ${formData.location.shortName}`
            : 'Location: not selected'}
        </Typography>
      )}
    </>
  );
}

export const EnvironmentLocationPickerFieldExtension = scaffolderPlugin.provide(
  createScaffolderFieldExtension<DeploymentContextValue | undefined>({
    name: 'EnvironmentLocationPicker',
    component: EnvironmentLocationPicker,
    schema: {
      returnValue: {
        type: 'object',
      },
    },
  }),
);
